import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_AVAILABILITY, type StaffMember } from '@acs/shared';
import {
  Avatar,
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  Field,
  Icon,
  IconButton,
  Input,
  Modal,
  PageHeader,
  Skeleton,
} from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import type { ServiceDoc, StaffDoc } from '../../shared/api/converters.js';
import {
  useBusinessAvailability,
  useReorderStaff,
  useServicesForStaff,
  useStaff,
  useUpsertStaffMember,
} from './hooks.js';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';

/**
 * Staff & hours (design template — Portal · Staff). The list doubles as the
 * booking priority order: when a caller has no preference, the first available
 * person from the top is booked — so reordering is a first-class control.
 */
export function StaffListPage() {
  const business = useActiveBusiness();
  const { data: staff, isLoading } = useStaff(business.id);
  const { data: services } = useServicesForStaff(business.id);
  const { data: availabilityDoc } = useBusinessAvailability(business.id);
  const [adding, setAdding] = useState(false);

  const { t } = useI18n();
  const rows = staff ?? [];
  const activeServices = (services ?? []).filter((s) => s.active);
  const availability = availabilityDoc ?? DEFAULT_AVAILABILITY;
  const hasBusinessHours = Object.values(availability.hours).some((a) => a && a.length > 0);
  const warnings = buildWarnings(rows, activeServices, hasBusinessHours, t);

  return (
    <>
      <PageHeader
        title={t('staff.title')}
        subtitle={t('staff.subtitle')}
        actions={
          <Button leadingIcon={<Icon name="plus" size={16} />} onClick={() => setAdding(true)}>
            {t('staff.addShort')}
          </Button>
        }
      />

      {warnings.length > 0 && (
        <div className="grid" style={{ marginBottom: 16 }}>
          {warnings.map((w, i) => (
            <Banner key={i} tone="warning" title={t('staff.cantBook')}>
              {w}
            </Banner>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="grid">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={76} radius="var(--tc-radius-md)" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Icon name="users" size={24} />}
          title={t('staff.empty.title')}
          description={t('staff.empty.description')}
          actions={
            <Button leadingIcon={<Icon name="plus" size={16} />} onClick={() => setAdding(true)}>
              {t('staff.addMember')}
            </Button>
          }
        />
      ) : (
        <Card flushBody>
          <ul className="staff-list" style={{ padding: '0 16px' }}>
            {rows.map((m, i) => (
              <StaffRow
                key={m.id}
                member={m}
                services={activeServices}
                first={i === 0}
                last={i === rows.length - 1}
                orderedIds={rows.map((r) => r.id)}
                index={i}
              />
            ))}
          </ul>
          {rows.length > 1 && (
            <p className="field-hint" style={{ padding: '0 16px 14px' }}>
              {t('staff.firstAvailableHint')}
            </p>
          )}
        </Card>
      )}

      {adding && (
        <AddStaffDialog
          businessId={business.id}
          nextSortOrder={rows.length}
          onClose={() => setAdding(false)}
        />
      )}
    </>
  );
}

function StaffRow({
  member,
  services,
  first,
  last,
  orderedIds,
  index,
}: {
  member: StaffDoc;
  services: ServiceDoc[];
  first: boolean;
  last: boolean;
  orderedIds: string[];
  index: number;
}) {
  const business = useActiveBusiness();
  const navigate = useNavigate();
  const reorder = useReorderStaff(business.id);
  const { t } = useI18n();

  const serviceSummary =
    member.serviceIds.length === 0
      ? t('staff.allServices')
      : services.filter((s) => member.serviceIds.includes(s.id)).map((s) => s.name).join(', ') || '—';

  const move = (dir: -1 | 1) => {
    const next = [...orderedIds];
    const j = index + dir;
    [next[index], next[j]] = [next[j]!, next[index]!];
    reorder.mutate(next);
  };

  // The whole row opens the editor — activating, deactivating and deleting
  // all live there now. Only the reorder arrows stay on the row, outside the
  // link, because the order IS this list (first available from the top).
  return (
    <li className="staff-row">
      <button
        type="button"
        className="staff-row-link"
        onClick={() => navigate(`/b/${business.id}/staff/${member.id}`)}
      >
        <Avatar name={member.name} />
        <div className="staff-row-main">
          <div className="staff-row-name">
            {member.name}
            {member.active ? (
              <Badge tone="success" dot size="sm">{t('staff.active')}</Badge>
            ) : (
              <Badge size="sm">{t('staff.inactive')}</Badge>
            )}
            <Badge tone={member.schedule ? 'info' : 'neutral'} size="sm">
              {t(member.schedule ? 'staff.customSchedule' : 'staff.businessHours')}
            </Badge>
          </div>
          <div className="staff-row-sub muted">{serviceSummary}</div>
        </div>
        <Icon name="chevron-right" size={18} className="staff-row-chevron" />
      </button>
      <div className="staff-row-actions">
        <IconButton
          size="sm"
          variant="ghost"
          aria-label={t('staff.moveUp', { name: member.name })}
          icon={<Icon name="chevron-up" size={16} />}
          disabled={first}
          onClick={() => move(-1)}
        />
        <IconButton
          size="sm"
          variant="ghost"
          aria-label={t('staff.moveDown', { name: member.name })}
          icon={<Icon name="chevron-down" size={16} />}
          disabled={last}
          onClick={() => move(1)}
        />
      </div>
    </li>
  );
}

/** Name-only creation, then straight into the schedule editor (doc 10 §3). */
function AddStaffDialog({
  businessId,
  nextSortOrder,
  onClose,
}: {
  businessId: string;
  nextSortOrder: number;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const upsert = useUpsertStaffMember(businessId);
  const [name, setName] = useState('');

  const create = () => {
    const staff: StaffMember = {
      name: name.trim(),
      active: true,
      sortOrder: nextSortOrder,
      serviceIds: [],
      schedule: null,
    };
    upsert.mutate({ staff }, { onSuccess: (id) => navigate(`/b/${businessId}/staff/${id}`) });
  };

  return (
    <Modal
      closeLabel={t('ui.closeDialog')}
      title={t('staff.addMember')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!name.trim()} loading={upsert.isPending} onClick={create}>
            {t('staff.addAndSetHours')}
          </Button>
        </>
      }
    >
      <Field label={t('clients.name')} required hint={t('staff.nameHint')}>
        <Input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('staff.namePlaceholder')}
          maxLength={80}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim()) create();
          }}
        />
      </Field>
    </Modal>
  );
}

function buildWarnings(
  staff: StaffDoc[],
  activeServices: ServiceDoc[],
  hasBusinessHours: boolean,
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
): string[] {
  const out: string[] = [];
  const activeStaff = staff.filter((s) => s.active);
  if (activeStaff.length === 0) return out; // unstaffed → business hours drive everything

  for (const svc of activeServices) {
    const covered = activeStaff.some((s) => s.serviceIds.length === 0 || s.serviceIds.includes(svc.id));
    if (!covered) out.push(t('staff.warning.uncovered', { service: svc.name }));
  }
  if (!hasBusinessHours) {
    const inheriting = activeStaff.filter((s) => s.schedule === null).map((s) => s.name);
    if (inheriting.length > 0) {
      out.push(t('staff.warning.noBusinessHours', { names: inheriting.join(', ') }));
    }
  }
  return out;
}
